import { useState, useMemo } from 'react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  Area,
  ComposedChart,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Build a recovery function from roasData
const buildRecoveryFunction = (roasData) => {
  if (!roasData || roasData.length === 0) {
    return () => 0
  }

  const roasMap = new Map()
  roasData.forEach((item) => {
    roasMap.set(item.day, item.roas / 100)
  })

  const maxDay = Math.max(...roasData.map((d) => d.day))
  const maxRoas = roasMap.get(maxDay) || 1

  return (day) => {
    if (roasMap.has(day)) {
      return roasMap.get(day)
    }

    if (day > maxDay) {
      return maxRoas
    }

    const sortedDays = [...roasMap.keys()].sort((a, b) => a - b)
    let lowerDay = 0
    let upperDay = sortedDays[sortedDays.length - 1]

    for (let i = 0; i < sortedDays.length; i++) {
      if (sortedDays[i] <= day) {
        lowerDay = sortedDays[i]
      }
      if (sortedDays[i] >= day && upperDay > day) {
        upperDay = sortedDays[i]
        break
      }
    }

    const lowerRoas = roasMap.get(lowerDay) || 0
    const upperRoas = roasMap.get(upperDay) || 0

    if (upperDay === lowerDay) return lowerRoas

    const ratio = (day - lowerDay) / (upperDay - lowerDay)
    return lowerRoas + (upperRoas - lowerRoas) * ratio
  }
}

const CompoundExpansion = ({ roasData }) => {
  const [initialFund, setInitialFund] = useState(100000)
  const [reinvestRate, setReinvestRate] = useState(100) // % of profit to reinvest
  const [targetRoi, setTargetRoi] = useState(100)
  const [simulationDays, setSimulationDays] = useState(365)

  const getRecoveryRate = useMemo(
    () => buildRecoveryFunction(roasData),
    [roasData],
  )

  // Compound expansion simulation
  // Every day, spend all available funds. Revenue comes back based on recovery model.
  // Reinvest a percentage of profits back into the fund pool
  const simulationData = useMemo(() => {
    const result = []
    let fundPool = initialFund
    let totalCost = 0
    let totalRevenue = 0

    // Track pending recoveries
    const pendingRecoveries = []

    for (let day = 1; day <= simulationDays; day++) {
      // Calculate today's revenue from all pending recoveries
      let todayRevenue = 0
      pendingRecoveries.forEach((recovery) => {
        const daysElapsed = day - recovery.daySpent
        const currentRecoveryRate = getRecoveryRate(daysElapsed)
        const prevRecoveryRate = getRecoveryRate(daysElapsed - 1)
        const dailyRecovery =
          (currentRecoveryRate - prevRecoveryRate) * recovery.amount
        todayRevenue += Math.max(0, dailyRecovery)
      })

      totalRevenue += todayRevenue

      // Calculate profit and reinvest
      const currentProfit = totalRevenue - totalCost
      const reinvestAmount =
        currentProfit > 0 ? (currentProfit * reinvestRate) / 100 : 0

      // Add revenue to fund pool (compound: all revenue goes back to fund pool)
      fundPool += todayRevenue

      // Spend all available funds (compound strategy)
      const todayCost = Math.max(0, fundPool)
      if (todayCost > 0) {
        fundPool -= todayCost
        totalCost += todayCost

        // Add to pending recoveries
        pendingRecoveries.push({
          daySpent: day,
          amount: todayCost,
        })
      }

      // Calculate ROI
      const roi = totalCost > 0 ? (totalRevenue / totalCost) * 100 : 0
      const profit = totalRevenue - totalCost
      const growthRate =
        initialFund > 0 ? ((totalRevenue - totalCost) / initialFund) * 100 : 0

      result.push({
        day,
        fundPool: Math.round(fundPool),
        todayCost: Math.round(todayCost),
        todayRevenue: Math.round(todayRevenue),
        totalCost: Math.round(totalCost),
        totalRevenue: Math.round(totalRevenue),
        profit: Math.round(profit),
        roi: roi.toFixed(2),
        growthRate: growthRate.toFixed(2),
        hitTarget: roi >= targetRoi,
      })
    }

    return result
  }, [initialFund, reinvestRate, targetRoi, simulationDays, getRecoveryRate])

  // Find breakeven day
  const breakevenDay = useMemo(() => {
    const day = simulationData.find((d) => d.hitTarget)
    return day?.day || null
  }, [simulationData])

  // Summary metrics
  const summary = useMemo(() => {
    const lastDay = simulationData[simulationData.length - 1]
    return {
      finalFundPool: lastDay?.fundPool || 0,
      totalCost: lastDay?.totalCost || 0,
      totalRevenue: lastDay?.totalRevenue || 0,
      profit: lastDay?.profit || 0,
      finalRoi: lastDay?.roi || 0,
      growthRate: lastDay?.growthRate || 0,
      breakevenDay,
      growthMultiple:
        initialFund > 0
          ? ((lastDay?.totalRevenue || 0) / initialFund).toFixed(2)
          : 0,
    }
  }, [simulationData, breakevenDay, initialFund])

  // Key milestones
  const milestones = useMemo(() => {
    const days = [30, 60, 90, 120, 180, 365]
    return days
      .filter((d) => d <= simulationDays)
      .map((d) => simulationData[d - 1])
      .filter(Boolean)
  }, [simulationData, simulationDays])

  if (!roasData || roasData.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
        No recovery model data available for simulation.
      </div>
    )
  }

  return (
    <div className="space-y-6 py-4">
      {/* Parameters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Compound Expansion Parameters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="initialFund">Initial Fund ($)</Label>
              <Input
                id="initialFund"
                type="number"
                value={initialFund}
                onChange={(e) => setInitialFund(Number(e.target.value))}
                placeholder="100000"
                min={0}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reinvestRate">Reinvest Rate (%)</Label>
              <Input
                id="reinvestRate"
                type="number"
                value={reinvestRate}
                onChange={(e) => setReinvestRate(Number(e.target.value))}
                placeholder="100"
                min={0}
                max={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="targetRoi">Target ROI (%)</Label>
              <Input
                id="targetRoi"
                type="number"
                value={targetRoi}
                onChange={(e) => setTargetRoi(Number(e.target.value))}
                placeholder="100"
                min={0}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="simulationDays">Simulation Days</Label>
              <Input
                id="simulationDays"
                type="number"
                value={simulationDays}
                onChange={(e) => setSimulationDays(Number(e.target.value))}
                placeholder="365"
                min={30}
                max={730}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Total Cost</div>
            <div className="text-2xl font-bold text-red-500">
              ${summary.totalCost.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Total Revenue</div>
            <div className="text-2xl font-bold text-green-600">
              ${summary.totalRevenue.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Profit</div>
            <div
              className={`text-2xl font-bold ${
                summary.profit >= 0 ? 'text-green-600' : 'text-red-500'
              }`}
            >
              ${summary.profit.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Final ROI</div>
            <div
              className={`text-2xl font-bold ${
                Number(summary.finalRoi) >= targetRoi
                  ? 'text-green-600'
                  : 'text-amber-600'
              }`}
            >
              {summary.finalRoi}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Growth Multiple</div>
            <div className="text-2xl font-bold text-purple-600">
              {summary.growthMultiple}x
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-slate-500">Breakeven Day</div>
            <div className="text-2xl font-bold text-blue-600">
              {summary.breakevenDay || 'N/A'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Growth Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Compound Growth Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={simulationData}>
              <defs>
                <linearGradient
                  id="revenueGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="day"
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `D${value}`}
              />
              <YAxis
                yAxisId="left"
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${(value / 1000).toFixed(2)}K`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-lg">
                        <p className="font-semibold">Day {data.day}</p>
                        <p className="text-sm text-red-500">
                          Total Cost: ${data.totalCost?.toLocaleString()}
                        </p>
                        <p className="text-sm text-green-600">
                          Total Revenue: ${data.totalRevenue?.toLocaleString()}
                        </p>
                        <p className="text-sm text-blue-600">
                          Profit: ${data.profit?.toLocaleString()}
                        </p>
                        <p className="text-sm text-purple-600">
                          ROI: {data.roi}%
                        </p>
                        <p className="text-sm text-amber-600">
                          Growth: {data.growthRate}%
                        </p>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <Legend />
              <ReferenceLine
                yAxisId="right"
                y={targetRoi}
                stroke="#22c55e"
                strokeDasharray="5 5"
                label={{
                  value: `Target ${targetRoi}%`,
                  position: 'right',
                  fontSize: 12,
                  fill: '#22c55e',
                }}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="totalRevenue"
                name="Total Revenue"
                stroke="#22c55e"
                fill="url(#revenueGradient)"
                strokeWidth={2}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="totalCost"
                name="Total Cost"
                stroke="#ef4444"
                fill="url(#costGradient)"
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="roi"
                name="ROI"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Milestones Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Key Milestones</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead className="text-right">Total Cost</TableHead>
                <TableHead className="text-right">Total Revenue</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">ROI</TableHead>
                <TableHead className="text-right">Growth</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {milestones.map((item) => (
                <TableRow key={item.day}>
                  <TableCell className="font-medium">Day {item.day}</TableCell>
                  <TableCell className="text-right text-red-500">
                    ${item.totalCost?.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right text-green-600">
                    ${item.totalRevenue?.toLocaleString()}
                  </TableCell>
                  <TableCell
                    className={`text-right ${
                      item.profit >= 0 ? 'text-green-600' : 'text-red-500'
                    }`}
                  >
                    ${item.profit?.toLocaleString()}
                  </TableCell>
                  <TableCell
                    className={`text-right font-semibold ${
                      Number(item.roi) >= targetRoi
                        ? 'text-green-600'
                        : 'text-amber-600'
                    }`}
                  >
                    {item.roi}%
                  </TableCell>
                  <TableCell className="text-right text-purple-600">
                    {item.growthRate}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

export default CompoundExpansion
